import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  DocumentHeader,
  DocumentSection,
  DocumentInfoGrid,
  DocumentTable,
  DocumentSignatureBlock,
  DocumentFooter,
  DocumentModalShell,
} from './DocumentBranding';
import { downloadElementAsPdf } from '../../../utils/documentPdfDownload';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
const fmtMoney = (n) =>
  n === null || n === undefined || n === '' ? '—' : `₦${Number(n).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`;
const yesNo = (b) => (b === true ? 'Yes' : b === false ? 'No' : '—');

const RfqDocumentView = ({ rfq, onClose }) => {
  const [downloading, setDownloading] = useState(false);

  if (!rfq) return null;

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await downloadElementAsPdf('procurement-document-content', `RFQ_${rfq.rfq_number}.pdf`);
    } catch (error) {
      console.error('Error generating RFQ PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const items = rfq.items || [];
  const itemRows = items.map((it, i) => [
    i + 1,
    it.description || '',
    it.specification || '',
    it.qty || '',
    it.unit || '',
    fmtMoney(it.unit_price),
    fmtMoney(it.total_price),
  ]);

  return (
    <DocumentModalShell onDownload={handleDownload} onClose={onClose} downloading={downloading}>
      <DocumentHeader
        title="REQUEST FOR QUOTATION"
        subtitle={`RFQ No. ${rfq.rfq_number}`}
      />

      <div className="bg-blue-50 border-b border-blue-200 px-10 py-4">
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-xs text-gray-600 font-semibold uppercase mb-1">RFQ Number</p>
            <p className="text-xl font-bold text-blue-900">{rfq.rfq_number}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Issue Date</p>
            <p className="text-lg font-semibold text-gray-900">{fmtDate(rfq.issue_date)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Submission Deadline</p>
            <p className="text-lg font-semibold text-gray-900">{fmtDate(rfq.submission_deadline)}</p>
          </div>
        </div>
      </div>

      <DocumentSection title="ISSUING COMPANY & BUYER">
        <DocumentInfoGrid
          pairs={[
            ['Issuing Company', rfq.issuing_company],
            ['Delivery Location', rfq.delivery_location],
            ['Buyer / Contact Person', rfq.buyer_contact_name],
            ['Delivery / Completion Date', fmtDate(rfq.delivery_completion_date)],
            ['Contact Email / Phone', rfq.buyer_contact_email_phone],
            ['Payment Terms Requested', rfq.payment_terms_requested],
            ['Validity Period Required', rfq.validity_period_required],
          ]}
        />
      </DocumentSection>

      <DocumentSection title="VENDOR INFORMATION">
        <DocumentInfoGrid
          pairs={[
            ['Vendor / Company Name', rfq.vendor_company_name],
            ['Contact Person', rfq.vendor_contact_person],
            ['Email', rfq.vendor_email],
            ['Phone', rfq.vendor_phone],
            ['Address', rfq.vendor_address],
            ['Tax / Registration No.', rfq.vendor_tax_reg_no],
          ]}
        />
      </DocumentSection>

      <DocumentSection title="SCOPE OF ITEMS / SERVICES REQUESTED">
        <DocumentTable
          headers={['#', 'Item / Service', 'Specification', 'Qty', 'Unit', 'Unit Price', 'Total']}
          rows={itemRows}
          alignRight={[5, 6]}
        />
        <div className="mt-4 flex justify-end">
          <div className="bg-green-50 px-6 py-3 rounded-lg border-2 border-green-600">
            <p className="text-xs text-green-800 font-semibold uppercase">Grand Total</p>
            <p className="text-2xl font-bold text-green-700">{fmtMoney(rfq.grand_total)}</p>
          </div>
        </div>
      </DocumentSection>

      <DocumentSection title="TERMS & CONDITIONS OFFERED BY VENDOR">
        <DocumentInfoGrid
          pairs={[
            ['Freight / Shipping Cost', rfq.freight_shipping_cost],
            ['Taxes / Duties Included?', yesNo(rfq.taxes_duties_included)],
            ['Warranty Period', rfq.warranty_period],
            ['Lead Time', rfq.lead_time],
            ['Payment Terms Offered', rfq.payment_terms_offered],
            ['Quote Valid Until', fmtDate(rfq.quote_valid_until)],
          ]}
        />
        <DocumentSignatureBlock labels={['Authorized Vendor Signature', 'Buyer / Procurement Signature']} />
      </DocumentSection>

      <DocumentFooter documentId={rfq.rfq_number} />
    </DocumentModalShell>
  );
};

export default RfqDocumentView;
